import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Download, CheckCircle2, XCircle, ClipboardList } from "lucide-react";
import { API, api } from "@/lib/api";
import { DiscordIcon } from "@/components/icons";

export const OrderResult = ({ state, data, sessionId }) => {
  // Direct link instead of async + window.open: mobile browsers block popups
  // that don't come straight from the tap, which broke downloads on phones.
  const downloadHref = `${API}/download-file?session_id=${encodeURIComponent(sessionId || "")}`;

  if (state === "checking") {
    return (
      <div className="text-center" data-testid="payment-checking">
        <Loader2 className="animate-spin mx-auto text-[#39FF14] mb-6" size={44} />
        <h1 className="font-anton text-3xl uppercase">Verifying payment…</h1>
        <p className="font-jet text-xs text-[#A1A1AA] mt-3">Hold tight. Don't close this page.</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="text-center" data-testid="payment-error">
        <XCircle className="mx-auto text-[#D90429] mb-6" size={44} />
        <h1 className="font-anton text-3xl uppercase red-text">Payment not confirmed</h1>
        <p className="font-jet text-xs text-[#A1A1AA] mt-3 max-w-md mx-auto">
          We couldn't verify your payment. If you were charged, contact us at aintdeady@gmail.com and we'll sort it out.
        </p>
        <Link to="/" className="btn-brutal inline-flex mt-8">Back to Site</Link>
      </div>
    );
  }

  // Service orders (coaching) ship no file — the call to action is the intake.
  const isService = data?.fulfillment === "service";

  return (
    <div className="text-center max-w-lg mx-auto" data-testid="payment-success">
      <CheckCircle2 className="mx-auto text-[#39FF14] mb-6" size={44} />
      <h1 className="font-anton text-4xl uppercase neon-text flicker">You're in.</h1>
      <p className="text-[#A1A1AA] text-sm mt-4 leading-relaxed">
        {isService
          ? "Payment confirmed. Your 4-week block starts now — two steps below. Your confirmation email carries your VIP code and the same intake link, so nothing is lost if you close this page."
          : "Payment confirmed. Grab your files below — your confirmation email contains your VIP code and a permanent download link, so nothing is lost if you close this page."}
      </p>
      <div className="border border-dashed border-[#39FF14] p-6 mt-8">
        <p className="font-jet text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-2">YOUR SECRET VIP CODE</p>
        <p data-testid="vip-code" className="font-anton text-3xl neon-text tracking-[0.2em]">{data?.vip_code}</p>
      </div>
      {isService && (
        <div className="border border-dashed border-[#39FF14] p-6 mt-8 text-left" data-testid="coaching-next-steps">
          <p className="section-label">// YOUR NEXT TWO STEPS //</p>
          <p className="font-bebas text-lg tracking-[0.15em] text-[#F5F5F5] mt-4">1 — COMPLETE YOUR INTAKE</p>
          <p className="text-[#A1A1AA] text-xs leading-relaxed">
            Five minutes. Your training plan and your first call are built on it.
          </p>
          <p className="font-bebas text-lg tracking-[0.15em] text-[#F5F5F5] mt-4">2 — JOIN THE DISCORD</p>
          <p className="text-[#A1A1AA] text-xs leading-relaxed">
            Post your VIP code. Your private coaching channel opens within 24 hours — that's where
            your 45-minute kickoff call happens.
          </p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-5 justify-center mt-10">
        {isService ? (
          <a
            data-testid="intake-btn"
            href={`/intake?session_id=${encodeURIComponent(sessionId || "")}`}
            className="btn-brutal"
          >
            <ClipboardList size={18} /> Complete Your Intake
          </a>
        ) : (
          <a data-testid="download-program-btn" href={downloadHref} className="btn-brutal">
            <Download size={18} /> Download Your Files
          </a>
        )}
        <a
          data-testid="discord-vip-btn"
          href={data?.discord_invite}
          target="_blank"
          rel="noreferrer"
          className="btn-brutal btn-brutal-green"
        >
          <DiscordIcon size={18} /> Join VIP Discord
        </a>
      </div>
      <p className="font-jet text-[10px] text-[#52525B] mt-8">
        Use your VIP code in Discord to unlock exclusive channels.
      </p>
      <Link to="/" className="font-jet text-xs text-[#A1A1AA] hover:text-[#39FF14] block mt-6">← Back to site</Link>
    </div>
  );
};

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data: d } = await api.get(`/checkout/status/${sessionId}`);
        if (d.payment_status === "paid") {
          setData(d);
          setState("paid");
          return;
        }
        if (d.status === "expired" || attempts.current >= 10) {
          setState("error");
          return;
        }
      } catch (e) {
        if (e.response?.status === 404 || attempts.current >= 10) {
          setState("error");
          return;
        }
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="success-page">
      <OrderResult state={state} data={data} sessionId={sessionId} />
    </div>
  );
}
